import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PostCard from "../components/PostCard";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
    Laptop, Heartbeat, GraduationCap, Plant, Car, Leaf, Robot,
    Storefront, CurrencyDollar, Tractor, BookOpen, Coffee, Sparkle,
    ArrowLeft, Lightbulb, Fire, TrendUp, ChatsCircle, Star, MapPin,
} from "@phosphor-icons/react";

const ICON_MAP = {
    Laptop, Heartbeat, GraduationCap, Plant, Car, Leaf, Robot,
    Storefront, CurrencyDollar, Tractor, BookOpen, Coffee, Sparkle,
};

export default function Category() {
    const { slug } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [category, setCategory] = useState(null);
    const [allCategories, setAllCategories] = useState([]);
    const [posts, setPosts] = useState([]);
    const [sort, setSort] = useState("trending");
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        api.get("/categories").then((r) => {
            setAllCategories(r.data);
            const c = r.data.find((x) => x.slug === slug);
            if (!c) setNotFound(true);
            setCategory(c || null);
        }).catch(() => setNotFound(true));
    }, [slug]);

    useEffect(() => {
        if (!category) return;
        setLoading(true);
        const params = {
            sort, limit: 30,
            author_role: "admin",
            category: category.name,
        };
        if (user?.country) params.country = user.country;
        api.get("/posts", { params })
            .then((r) => setPosts(r.data))
            .finally(() => setLoading(false));
    }, [sort, category, user]);

    if (notFound) {
        return (
            <div className="px-4 sm:px-6 lg:px-10 py-12 max-w-3xl mx-auto text-center" data-testid="category-not-found">
                <Lightbulb size={36} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
                <h1 className="font-display font-black text-3xl tracking-tighter mb-2 brand-text">Category not found</h1>
                <p className="text-muted-foreground mb-6">This category may have been removed.</p>
                <Button onClick={() => navigate("/")} className="brand-button" data-testid="back-home">
                    <ArrowLeft size={16} className="mr-1" /> Back to Home
                </Button>
            </div>
        );
    }

    if (!category) {
        return (
            <div className="px-4 sm:px-6 lg:px-10 py-12 max-w-5xl mx-auto" data-testid="category-loading">
                <div className="h-10 w-64 rounded-lg bg-card border border-border animate-pulse mb-4" />
                <div className="h-5 w-96 rounded-lg bg-card border border-border animate-pulse" />
            </div>
        );
    }

    const Icon = ICON_MAP[category.icon] || Lightbulb;
    const others = allCategories.filter((c) => c.slug !== category.slug).slice(0, 6);

    return (
        <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-5xl mx-auto" data-testid={`category-page-${category.slug}`}>
            <SEO
                title={`${category.name} startup ideas`}
                description={category.description || `Curated ${category.name} startup ideas and unsolved problems. Discover what to build next in ${category.name}.`}
                path={`/category/${category.slug}`}
            />
            <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="mb-4 text-muted-foreground hover:text-foreground"
                data-testid="back-to-home"
            >
                <ArrowLeft size={16} className="mr-1" /> Home
            </Button>

            <header className="mb-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <div className="flex items-start gap-4">
                    <div className="h-14 w-14 rounded-xl bg-[#6C63FF]/10 text-[#6C63FF] grid place-items-center shrink-0">
                        <Icon size={28} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF8A00] mb-1">
                            Editor's picks
                        </div>
                        <h1 className="font-display font-black text-4xl lg:text-5xl leading-[0.95] tracking-tighter brand-text">
                            {category.name}
                        </h1>
                        {category.description && (
                            <p className="text-muted-foreground mt-2 text-base">{category.description}</p>
                        )}
                        {user?.country && (
                            <Badge className="bg-[#6C63FF]/10 text-[#6C63FF] hover:bg-[#6C63FF]/10 border-[#6C63FF]/20 mt-3">
                                <MapPin size={12} weight="duotone" className="mr-1" /> {user.country}
                            </Badge>
                        )}
                    </div>
                </div>
                <Link
                    to={`/feed?category=${encodeURIComponent(category.name)}`}
                    className="text-sm text-[#6C63FF] hover:underline shrink-0"
                    data-testid="see-community-in-category"
                >
                    Community ideas in {category.name} →
                </Link>
            </header>

            <Tabs value={sort} onValueChange={setSort}>
                <TabsList className="mb-6" data-testid="category-sort-tabs">
                    <TabsTrigger value="trending" data-testid="cat-sort-trending"><Fire size={14} weight="duotone" className="mr-1.5" /> Trending</TabsTrigger>
                    <TabsTrigger value="latest" data-testid="cat-sort-latest"><TrendUp size={14} weight="duotone" className="mr-1.5" /> Latest</TabsTrigger>
                    <TabsTrigger value="discussed" data-testid="cat-sort-discussed"><ChatsCircle size={14} weight="duotone" className="mr-1.5" /> Most discussed</TabsTrigger>
                    <TabsTrigger value="featured" data-testid="cat-sort-featured"><Star size={14} weight="duotone" className="mr-1.5" /> Featured</TabsTrigger>
                </TabsList>
                <TabsContent value={sort}>
                    {loading ? (
                        <div className="grid gap-4">
                            {[1,2,3].map(i => <div key={i} className="h-48 rounded-2xl border border-border bg-card animate-pulse" />)}
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-border p-12 text-center">
                            <Lightbulb size={36} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
                            <h3 className="font-display font-bold text-lg mb-1">No editor picks yet in {category.name}</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Our team hasn't curated any problems here for {user?.country || "your country"} yet.
                            </p>
                            <Link to={`/feed?category=${encodeURIComponent(category.name)}`}>
                                <Button className="brand-button" data-testid="empty-go-community">
                                    Explore community ideas in {category.name}
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <div className="grid gap-5">
                            {posts.map((p) => <PostCard key={p.id} post={p} />)}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Related categories */}
            {others.length > 0 && (
                <section className="mt-14">
                    <h2 className="font-display font-bold text-xl mb-4 tracking-tight">Other categories</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {others.map((c) => {
                            const I = ICON_MAP[c.icon] || Lightbulb;
                            return (
                                <Link
                                    key={c.id}
                                    to={`/category/${c.slug}`}
                                    data-testid={`related-category-${c.slug}`}
                                    className="idea-card rounded-xl border border-border bg-card p-4 flex flex-col gap-3"
                                >
                                    <div className="h-9 w-9 rounded-lg bg-[#6C63FF]/10 text-[#6C63FF] grid place-items-center">
                                        <I size={18} weight="duotone" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-sm leading-tight">{c.name}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.description}</div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}
